import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBaseUrl } from './ApiContext';
import './CompanyTimeline.css';

const baseUrl = getBaseUrl();

// What past-deal monitoring found since we looked at this company, and every
// change to its monitoring status. Written as sentences rather than rows:
// public news only, said once in the heading, because the spec asks that it
// never read as something a founder told us privately.
//
// `data` is the already-fetched timeline (the company page has it, because
// the same response carries the header's sentences). Without it, this
// fetches for itself, which is how the deal page uses it.

const SHOWN = 6;

function day(iso) {
    // Parsed at local noon so a US timezone never shows the day before.
    return new Date(`${iso}T12:00:00`);
}

function shortDate(iso) {
    const d = day(iso);
    const thisYear = new Date().getFullYear();
    return d.toLocaleDateString(undefined, {
        day: 'numeric', month: 'short',
        ...(d.getFullYear() === thisYear ? {} : { year: 'numeric' }),
    });
}

function Event({ event }) {
    const meta = [];
    if (event.source_url) {
        meta.push(
            <a key="src" href={event.source_url} target="_blank" rel="noopener noreferrer">
                {event.source}
            </a>);
        if (event.more_sources > 0) meta.push(<span key="more">+{event.more_sources} more</span>);
    }
    if (event.type === 'status' && event.via) meta.push(<span key="by">{event.via}</span>);

    return (
        <li className={`ct-event${event.type === 'status' ? ' is-status' : ''}`}>
            <p className="ct-line">
                <span className="ct-head">{event.headline}</span>
                {event.type === 'development' && event.via ? ` (via ${event.via})` : ''}
                {event.detail && <span className="ct-detail"> {event.detail}</span>}
                {event.confidence_note && (
                    <span className="ct-flag" title={event.confidence_note}> unconfirmed</span>
                )}
                <span className="ct-when">
                    {' '}{event.date_basis === 'found' ? 'found ' : ''}{shortDate(event.date)}
                    {meta.length > 0 && ' · '}
                    {meta.reduce((out, item, i) => (
                        i ? [...out, <span key={`s${i}`}> · </span>, item] : [item]
                    ), [])}
                </span>
            </p>
        </li>
    );
}

function CompanyTimeline({ companyId, version = 0, data = null }) {
    const [fetched, setFetched] = useState(null);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!companyId || data) return undefined;
        let live = true;
        setFetched(null);
        setError(null);
        axios.get(`${baseUrl}/api/companies/${companyId}/timeline`)
            .then(response => { if (live) setFetched(response.data); })
            .catch(err => {
                console.error('Error fetching company timeline:', err);
                if (live) setError('Could not load what has happened since.');
            });
        return () => { live = false; };
    }, [companyId, version, data]);

    if (!companyId) return null;

    const timeline = data || fetched;
    const events = timeline ? timeline.events : [];
    const visible = expanded ? events : events.slice(0, SHOWN);

    return (
        <section className="co-section">
            <h2>
                Since we looked
                {timeline && events.length > 0 && <span className="co-count">{events.length}</span>}
            </h2>
            {error && <div className="co-empty">{error}</div>}
            {!error && !timeline && <div className="co-empty">Loading…</div>}
            {timeline && !events.length && (
                <div className="co-empty">Nothing found yet. The weekly scan checks every company.</div>
            )}
            {visible.length > 0 && (
                <ul className="ct-list">
                    {visible.map(event => (
                        <Event key={`${event.type}-${event.id}`} event={event} />
                    ))}
                </ul>
            )}
            {events.length > SHOWN && (
                <button type="button" className="co-more" onClick={() => setExpanded(e => !e)}>
                    {expanded ? 'Show fewer' : `Show ${events.length - SHOWN} more`}
                </button>
            )}
        </section>
    );
}

export default CompanyTimeline;
